import { useEffect, useRef, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Button } from "@/components/design/Button";
import {
  EMPTY_TALENT_FILTERS,
  fetchTalentPhotos,
  fetchTalents,
  type TobisPersonRef,
  type TobisTalent,
} from "@/lib/tobis";
import { TobisPersonRow } from "./TobisPersonRow";
import { resolvePersons, type ResolvedPerson } from "./tobis-lab-resolve-persons";
import { SHOW_PERSON_AI_MAPPING } from "./lab-visibility";

// Personen-Auswahl fuer die Pitch-Kriterien (Regie/Drehbuchautor:in/Cast) - aus
// der bestehenden Talent-Liste ODER per Freitext, das die KI auf echte
// Personen mapped (Session-Entscheidung: beide Wege, kein Entweder-Oder).
//
// max=1 (Regie/Autor:in) ersetzt die bisherige Auswahl statt sie zu ergaenzen,
// max=undefined (Cast) haengt an. Rendering ueber TobisPersonRow (embedded) -
// keine neue Chip-Optik erfunden, dieselbe Zeile wie ueberall sonst in Tobis.

const SEARCH_DEBOUNCE_MS = 250;
const SEARCH_LIMIT = 6;

function RemoveButton({ onClick, label }: { onClick: () => void; label: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      className="rounded-full p-1 text-ink/40 transition hover:bg-red-600/10 hover:text-red-600"
    >
      ×
    </button>
  );
}

function AddButton({ onClick, label }: { onClick: () => void; label: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      className="rounded-full p-1 text-ink/40 transition hover:bg-accent/10 hover:text-accent"
    >
      +
    </button>
  );
}

export function TobisPersonPicker({
  label,
  placeholder,
  value,
  onChange,
  max,
}: {
  label: string;
  placeholder: string;
  value: readonly TobisPersonRef[];
  onChange: (next: TobisPersonRef[]) => void;
  /** Ungesetzt = beliebig viele (Cast). 1 = Einzelauswahl (Regie/Autor:in): eine neue Auswahl ersetzt die vorherige. */
  max?: number;
}) {
  const intl = useIntl();
  const [query, setQuery] = useState("");
  const [searchResults, setSearchResults] = useState<TobisTalent[]>([]);
  const [searching, setSearching] = useState(false);
  const [aiMode, setAiMode] = useState(false);
  const [aiText, setAiText] = useState("");
  const [aiResults, setAiResults] = useState<ResolvedPerson[]>([]);
  const [aiWorking, setAiWorking] = useState(false);
  // Nur fuers Foto: TobisPersonRef (value/onChange, auch was in criteria persistiert
  // und an findComparables geschickt wird) fuehrt bewusst nur id+name, siehe dortigen
  // Kommentar. Der Suchtreffer hat das Foto schon geladen (TobisTalent.photoUrl) - hier
  // zwischengespeichert, damit die ausgewaehlte Zeile es zeigen kann, ohne die
  // Datenform der eigentlichen Auswahl zu erweitern. null (nicht "fehlt im Objekt")
  // heisst explizit "geprueft, kein Foto gefunden" - verhindert wiederholte Nachlade-
  // Versuche fuer dieselbe Person.
  const [photoByPersonId, setPhotoByPersonId] = useState<Record<string, string | null>>({});
  // Nur fuer den Nachlade-Effekt unten: personIds, fuer die schon eine Suche
  // laeuft. Ref statt State - soll den Request nicht doppelt ausloesen,
  // React-StrictMode ruft den Effekt beim Mount zweimal auf (Dev-Modus), das
  // `cancelled`-Flag im Effekt selbst haelt zwar die zweite State-Uebernahme
  // ab, aber nicht den zweiten echten Netzwerk-Request - genau das hat den
  // Verbindungsengpass verursacht (Marvin, 2026-08-07).
  const photoFetchInFlight = useRef<Set<string>>(new Set());

  const atMax = max !== undefined && value.length >= max;
  const selectedIds = new Set(value.map((p) => p.personId));

  // Fotos beim Oeffnen einer gespeicherten Untersuchung nachladen (Marvin,
  // 2026-08-07): value kommt dann aus session.criteria (nur id+name, siehe
  // Kommentar an photoByPersonId), ohne diesen Effekt blieben es dauerhaft
  // Initialen. Ein gebatchter ID-Lookup (fetchTalentPhotos) statt einer
  // Namenssuche pro Person (Marvin, 2026-08-07): die urspruengliche Variante
  // umging die teure Einzel-Personen-Route (/tobis-api/talents/:personId,
  // haengt Credits/Skripte + Live-TMDB-Nachladung an), war dabei aber selbst
  // langsam - N parallele Volltextsuchen gegen die ganze Talent-Tabelle statt
  // eines einzelnen indexierten Lookups per ID, den wir schon haben (siehe
  // getTalentPhotos im BFF).
  //
  // Bewusst KEIN "cancelled"-Flag, das das setPhotoByPersonId unten blockt
  // (Marvin, 2026-08-07): React ruft diesen Effekt im Dev-Modus (StrictMode)
  // beim Mount zweimal auf (mount -> cleanup -> mount). Der ECHTE Request des
  // ersten Laufs laeuft im Hintergrund weiter, sein Cleanup wuerde aber ein
  // "cancelled"-Flag setzen, das die Antwort spaeter verwirft - Ergebnis:
  // dauerhaft Initialen, bis `value` sich mal aendert und der Effekt ohne
  // stoerenden Doppel-Lauf erneut feuert (genau der beobachtete "Fotos
  // erscheinen erst nach dem Loeschen"-Bug). photoFetchInFlight verhindert
  // bereits einen zweiten ECHTEN Request fuer dieselben IDs - das reicht als
  // Schutz, das Ergebnis des einzigen laufenden Requests darf immer ins
  // Zwischenspeicher, auch nach einem (Dev-only) Remount.
  useEffect(() => {
    const missing = value.filter(
      (p) => !(p.personId in photoByPersonId) && !photoFetchInFlight.current.has(p.personId),
    );
    if (missing.length === 0) return;
    for (const p of missing) photoFetchInFlight.current.add(p.personId);
    void fetchTalentPhotos(missing.map((p) => p.personId))
      .catch(() => ({}) as Record<string, string | null>)
      .then((photos) => {
        for (const p of missing) photoFetchInFlight.current.delete(p.personId);
        setPhotoByPersonId((prev) => {
          const next = { ...prev };
          for (const p of missing) next[p.personId] = photos[p.personId] ?? null;
          return next;
        });
      });
    // Nicht auf value selbst: fuer Regie/Autor:in baut der Aufrufer es als
    // `director ? [director] : []` - ein frisches Array-Literal bei JEDEM
    // Render, auch wenn sich nichts geaendert hat. Mit value als Dependency
    // haette das den Effekt bei jedem Tastendruck irgendwo im Formular erneut
    // ausgeloest (Marvin, 2026-08-07: DB-Last durch genau diesen Bug).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value.map((p) => p.personId).join(",")]);

  useEffect(() => {
    if (!query.trim() || atMax) {
      setSearchResults([]);
      setSearching(false);
      return;
    }
    let cancelled = false;
    // Erst NACH dem Debounce auf "suchend" schalten, nicht schon beim Tippen:
    // sonst blinkt der Spinner bei jedem Tastendruck kurz auf, auch wenn der
    // Timer noch gar nicht abgelaufen ist.
    const timer = window.setTimeout(() => {
      if (cancelled) return;
      setSearching(true);
      void fetchTalents({ ...EMPTY_TALENT_FILTERS, search: query.trim() }, SEARCH_LIMIT)
        .then((page) => {
          if (!cancelled) setSearchResults(page.talents);
        })
        .catch(() => {
          if (!cancelled) setSearchResults([]);
        })
        .finally(() => {
          if (!cancelled) setSearching(false);
        });
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [query, atMax]);

  function add(person: TobisPersonRef, photoUrl?: string | null) {
    if (selectedIds.has(person.personId)) return;
    if (photoUrl !== undefined) {
      setPhotoByPersonId((prev) => ({ ...prev, [person.personId]: photoUrl }));
    }
    onChange(max === 1 ? [person] : [...value, person]);
    setQuery("");
    setSearchResults([]);
    setAiResults((prev) => prev.filter((p) => p.personId !== person.personId));
  }

  function remove(personId: string) {
    onChange(value.filter((p) => p.personId !== personId));
  }

  async function handleResolve() {
    if (!aiText.trim() || aiWorking) return;
    setAiWorking(true);
    try {
      const matches = await resolvePersons(aiText);
      setAiResults(matches.filter((m) => !selectedIds.has(m.personId)));
    } catch {
      setAiResults([]);
    } finally {
      setAiWorking(false);
    }
  }

  return (
    <div>
      <div className="mb-1 text-[10px] tracking-[0.25em] text-ink/55">{label}</div>

      {value.length > 0 ? (
        <div className="mb-2 space-y-1.5">
          {value.map((p) => (
            <TobisPersonRow
              key={p.personId}
              name={p.personName}
              photoUrl={photoByPersonId[p.personId] ?? null}
              title={p.personName ?? p.personId}
              embedded
              actions={
                <RemoveButton
                  onClick={() => remove(p.personId)}
                  label={intl.formatMessage(
                    { id: "tobis.lab.personRemove", defaultMessage: "Remove {name}" },
                    { name: p.personName ?? p.personId },
                  )}
                />
              }
            />
          ))}
        </div>
      ) : null}

      {!atMax ? (
        <div className="space-y-2">
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={placeholder}
            className="w-full rounded-md bg-page px-3 py-1.5 text-[13px] outline-none ring-1 ring-ink/10 focus-visible:ring-ink/30"
          />
          {searching ? (
            <div className="flex items-center gap-2 text-[12px] text-ink/45">
              <span
                aria-hidden
                className="inline-block h-2.5 w-2.5 shrink-0 animate-spin rounded-full border-2 border-current border-t-transparent"
              />
              <FormattedMessage id="tobis.lab.personSearching" defaultMessage="Searching…" />
            </div>
          ) : null}
          {searchResults.length > 0 ? (
            <div className="space-y-1">
              {searchResults.map((t) => (
                <TobisPersonRow
                  key={t.personId}
                  name={t.name}
                  photoUrl={t.photoUrl}
                  title={t.name ?? t.personId}
                  embedded
                  actions={
                    <AddButton
                      onClick={() => add({ personId: t.personId, personName: t.name }, t.photoUrl)}
                      label={intl.formatMessage(
                        { id: "tobis.lab.personAdd", defaultMessage: "Add {name}" },
                        { name: t.name ?? t.personId },
                      )}
                    />
                  }
                />
              ))}
            </div>
          ) : null}

          {SHOW_PERSON_AI_MAPPING ? (
            <button
              type="button"
              onClick={() => setAiMode((v) => !v)}
              className="text-[11px] text-ink/45 underline decoration-ink/20 underline-offset-2 transition hover:text-ink"
            >
              {aiMode ? (
                <FormattedMessage id="common.cancel" defaultMessage="Cancel" />
              ) : (
                <FormattedMessage id="tobis.lab.personAiToggle" defaultMessage="Type text, AI maps it" />
              )}
            </button>
          ) : null}

          {SHOW_PERSON_AI_MAPPING && aiMode ? (
            <div className="space-y-2 rounded-lg bg-accent/5 p-3">
              <textarea
                value={aiText}
                onChange={(e) => setAiText(e.target.value)}
                placeholder={intl.formatMessage({
                  id: "tobis.lab.personAiPlaceholder",
                  defaultMessage: "e.g. lead played by an actor like Jane Doe, directed by John Smith",
                })}
                rows={2}
                className="field-sizing-content w-full resize-none rounded-md bg-page p-2 text-[13px] leading-relaxed outline-none ring-1 ring-ink/10 focus-visible:ring-ink/30"
              />
              <Button
                variant="secondary"
                size="compact"
                onClick={() => void handleResolve()}
                disabled={!aiText.trim() || aiWorking}
                aria-busy={aiWorking}
              >
                {aiWorking ? (
                  <FormattedMessage id="tobis.lab.aiWorking" defaultMessage="Working…" />
                ) : (
                  <FormattedMessage id="tobis.lab.personAiButton" defaultMessage="Map with AI" />
                )}
              </Button>
              {aiResults.length > 0 ? (
                <div className="space-y-1">
                  {aiResults.map((r) => (
                    <TobisPersonRow
                      key={r.personId}
                      name={r.name}
                      photoUrl={null}
                      title={r.name ?? r.personId}
                      embedded
                      actions={
                        <AddButton
                          onClick={() => add({ personId: r.personId, personName: r.name })}
                          label={intl.formatMessage(
                            { id: "tobis.lab.personAdd", defaultMessage: "Add {name}" },
                            { name: r.name ?? r.personId },
                          )}
                        />
                      }
                    />
                  ))}
                </div>
              ) : !aiWorking && aiText.trim() ? (
                <div className="text-[12px] text-ink/45">
                  <FormattedMessage id="tobis.lab.personAiNoMatches" defaultMessage="No matches yet." />
                </div>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
